import { Activity, Send, CheckCircle2, Eye, XCircle } from "lucide-react";
import { getPercentage } from "../constants/campaignConstants";

export function toUnixSeconds(value) {
  const n = Number(value);
  if (!n) return null;
  return n > 9999999999 ? Math.floor(n / 1000) : Math.floor(n);
}

export function buildCampaignAnalyticsDuration(campaign) {
  const nowSec = Math.floor(Date.now() / 1000);
  const createdSec = campaign?.created_at ? toUnixSeconds(campaign.created_at) : null;
  const endSec = campaign?.completed_at ? toUnixSeconds(campaign.completed_at) : nowSec;
  const startSec = createdSec ?? (endSec - 30 * 24 * 60 * 60);
  return { start: startSec, end: endSec };
}

export function parseCampaignAnalyticsDatapoints(res) {
  if (Array.isArray(res?.data?.datapoints)) return res.data.datapoints;
  if (Array.isArray(res?.datapoints)) return res.datapoints;
  return [];
}

export function aggregateCampaignDatapoints(datapoints = []) {
  return datapoints.reduce(
    (acc, point) => ({
      sent: acc.sent + Number(point.sent || 0),
      delivered: acc.delivered + Number(point.delivered || 0),
      read: acc.read + Number(point.read || 0),
      failed: acc.failed + Number(point.failed || 0),
      attempted: acc.attempted + Number(point.attempted || 0),
    }),
    { sent: 0, delivered: 0, read: 0, failed: 0, attempted: 0 }
  );
}

export function buildCampaignFunnelData(totals) {
  const { attempted, sent, delivered, read, failed } = totals;
  return [
    { label: "Attempted", value: attempted, color: "bg-slate-400 dark:bg-slate-500", prevValue: null },
    { label: "Sent", value: sent, color: "bg-blue-400 dark:bg-blue-500", prevValue: attempted || null },
    { label: "Delivered", value: delivered, color: "bg-[hsl(158,48%,38%)] dark:bg-[hsl(158,48%,42%)]", prevValue: sent || null },
    { label: "Read", value: read, color: "bg-indigo-400 dark:bg-indigo-500", prevValue: delivered || null },
    { label: "Failed", value: failed, color: "bg-red-400 dark:bg-red-500", prevValue: attempted || null },
  ];
}

export function sortCampaignDatapoints(datapoints = []) {
  return [...datapoints].sort(
    (a, b) => toUnixSeconds(a.date) - toUnixSeconds(b.date)
  );
}

export function formatCampaignDatapointDate(date) {
  const seconds = toUnixSeconds(date);
  if (!seconds) return "—";
  return new Date(seconds * 1000).toLocaleDateString("en-IN", {
    month: "short",
    day: "numeric",
  });
}

export function buildCampaignStatsData(totals) {
  return [
    {
      label: "Attempted",
      value: totals.attempted,
      pct: "",
      icon: Activity,
      color: "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
    },
    {
      label: "Sent",
      value: totals.sent,
      pct: getPercentage(totals.sent, totals.attempted),
      icon: Send,
      color: "text-gray-600 bg-gray-100 dark:bg-slate-800 dark:text-gray-300",
    },
    {
      label: "Delivered",
      value: totals.delivered,
      pct: getPercentage(totals.delivered, totals.sent),
      icon: CheckCircle2,
      color: "text-[hsl(158,48%,34%)] bg-[hsl(158,48%,38%)]/10 dark:bg-[hsl(158,48%,42%)]/15 dark:text-[hsl(158,45%,55%)]",
    },
    {
      label: "Read",
      value: totals.read,
      pct: getPercentage(totals.read, totals.delivered),
      icon: Eye,
      color: "text-blue-600 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-400",
    },
    {
      label: "Failed",
      value: totals.failed,
      pct: getPercentage(totals.failed, totals.attempted),
      icon: XCircle,
      color: "text-red-600 bg-red-50 dark:bg-rose-950/30 dark:text-rose-400",
    },
  ];
}
